import "./Delivery.scss";
import { CellData, DimensionMap } from "../MetricsTable/metricsTableUtils";
import { DailyDeliveryData } from "../../StreamingDelivery/StreamingDelivery";
import { DateGroupingKey } from "../../TVADCrossChannel/homePageConstants";
import { DateRange, StateSetter } from "../../utils/types";
import { getMetricFormatter } from "../../utils/metric-format-utils";
// import { Metric } from "@blisspointmedia/bpm-types/dist/CrossChannel";
import { SingleChannelMediaTypes } from "../../Performance/performanceUtils";
import { Spinner } from "../../Components";
import * as R from "ramda";
import PercentChange from "../PercentChange";
import React, { useMemo } from "react";
import { DimensionColumn } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import SingleChannelDeliveryChart, {
  DEFAULT_DELIVERY_METRICS_OPTIONS,
  DeliveryMetricOption,
} from "./SingleChannelDeliveryChart";
import {
  getCompanyChannelColumnMetaData,
  sharedColumnMetaDataMap,
} from "../Channels/sharedPerformanceUtils";

const OVERVIEW_KEYS: [string, string, string] = ["spend", "impressions", "cpm"];

interface DeliveryProps {
  company: string;
  data: DailyDeliveryData;
  dates: DateRange;
  defaultDateGroupingKey?: DateGroupingKey;
  defaultMetric?: string;
  deliveryMetricOptions?: DeliveryMetricOption[];
  dimension: string;
  dimensionOptions: string[];
  getDimensionCell: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args: any[]
  ) => CellData;
  otherData: DailyDeliveryData;
  otherDates?: DateRange;
  prefix: SingleChannelMediaTypes;

  setDimension: StateSetter<string>;
  overviewKeys?: [string, string, string];
}

const Delivery = ({
  company,
  data,
  dates,
  defaultDateGroupingKey,
  defaultMetric,
  deliveryMetricOptions = DEFAULT_DELIVERY_METRICS_OPTIONS,
  dimension,
  dimensionOptions,
  getDimensionCell,
  otherData,
  otherDates,
  prefix,
  setDimension,
  overviewKeys = OVERVIEW_KEYS,
}: DeliveryProps): JSX.Element => {
  const metricFormatter = useMemo(() => getMetricFormatter(company), [company]);
  const columnMetaData = useMemo(
    () => getCompanyChannelColumnMetaData(sharedColumnMetaDataMap, company),
    [company]
  );
  const overview = useMemo(() => {
    const overviewData: Record<
      string,
      { primary: number; comparison: number }
    > = overviewKeys.reduce((data, item) => {
      data[item] = {
        primary: 0,
        comparison: 0,
      };
      return data;
    }, {});

    for (const option of deliveryMetricOptions) {
      overviewData[option.value] = { primary: 0, comparison: 0 };
    }
    const primaryData = !R.isNil(data)
      ? !R.isNil(data.daily) && R.isEmpty(data.daily) && !R.isEmpty(data.weekly)
        ? data.weekly
        : data.daily
      : {};
    if (primaryData) {
      for (const date of R.keys(primaryData)) {
        if (primaryData[date]) {
          for (const dimension of R.keys(primaryData[date])) {
            if (primaryData[date][dimension]) {
              for (const option of deliveryMetricOptions) {
                overviewData[option.value].primary =
                  R.defaultTo(0, overviewData[option.value].primary) +
                  R.defaultTo(0, primaryData[date][dimension][option.value]);
              }
            }
          }
        }
      }
    }
    const comparisonData = !R.isNil(otherData)
      ? !R.isNil(otherData.daily) && R.isEmpty(otherData.daily) && !R.isEmpty(otherData.weekly)
        ? otherData.weekly
        : otherData.daily
      : {};
    if (comparisonData) {
      for (const date of R.keys(comparisonData)) {
        if (comparisonData[date]) {
          for (const dimension of R.keys(comparisonData[date])) {
            if (comparisonData[date][dimension]) {
              for (const option of deliveryMetricOptions) {
                overviewData[option.value].comparison =
                  R.defaultTo(0, overviewData[option.value].comparison) +
                  R.defaultTo(0, comparisonData[date][dimension][option.value]);
              }
            }
          }
        }
      }
    }
    for (const metric of overviewKeys) {
      const metaData = columnMetaData[metric];
      if (metaData && metaData.requiredTotalsColumns && metaData.aggregator) {
        const { aggregator } = metaData;
        const [primaryDataObj, comparisonDataObj] = metaData.requiredTotalsColumns.reduce(
          ([pObj, cObj], col) => {
            if (typeof col === "string") {
              pObj[col] = overviewData[col] ? overviewData[col].primary : 0;
              cObj[col] = overviewData[col] ? overviewData[col].comparison : 0;
            }
            return [pObj, cObj];
          },
          [{}, {}]
        );
        const primaryVal = aggregator(primaryDataObj);
        const compVal = aggregator(comparisonDataObj);
        overviewData[metric].primary = typeof primaryVal === "number" ? primaryVal : 0;
        overviewData[metric].comparison = typeof compVal === "number" ? compVal : 0;
      }
    }
    if (data && (otherData || R.isNil(otherDates))) {
      return (
        <div className="deliveryOverview">
          {R.map(key => {
            const percentChange =
              overviewData[key].primary && overviewData[key].comparison
                ? ((overviewData[key].primary - overviewData[key].comparison) /
                    overviewData[key].comparison) *
                  100
                : 0;
            const value = metricFormatter(key)
              ? metricFormatter(key)(overviewData[key].primary)
              : metricFormatter("number")(overviewData[key].primary);

            const isSummedTotal =
              columnMetaData[key] && !columnMetaData[key]?.requiredTotalsColumns;
            const nameToDisplay =
              key === "impressions" ? "Impressions" : columnMetaData[key]?.displayName || key;
            return (
              <div className="deliveryOverviewItem" key={`deliveryOverviewItem${value}`}>
                <div className="overviewValueKeyContainer">
                  <div className="overviewValue">{value}</div>
                  <div className="overviewKey">{nameToDisplay}</div>
                </div>
                {isSummedTotal && <div className="overviewType">Total</div>}
                <div className="percentChangeContainer">
                  <PercentChange percentChange={percentChange} size={"lg"} />
                </div>
              </div>
            );
          }, overviewKeys)}
        </div>
      );
    }
  }, [
    data,
    deliveryMetricOptions,
    otherData,
    otherDates,
    metricFormatter,
    overviewKeys,
    columnMetaData,
  ]);

  return R.isNil(overview) || R.isNil(data) || R.isNil(otherData && otherDates) ? (
    <Spinner size={100} />
  ) : (
    <div className="deliveryTab">
      {overview}
      <SingleChannelDeliveryChart
        company={company}
        data={data}
        dates={dates}
        defaultDateGroupingKey={defaultDateGroupingKey}
        defaultMetric={defaultMetric}
        deliveryMetricOptions={deliveryMetricOptions}
        dimension={dimension}
        dimensionOptions={dimensionOptions}
        getDimensionCell={getDimensionCell}
        otherData={otherData}
        otherDates={otherDates}
        prefix={prefix}
        setDimension={setDimension}
        efficienyMetric={overviewKeys[2]}
      />
    </div>
  );
};

export default Delivery;
